import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CardBody, Row, Col, Card, Container, Input } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import BreadCrumb from '../../../../Components/Common/BreadCrumb';
import ActionButton from '../../../../Components/Common/ActionButton';
import * as api from './procurementApi';
import { formatDateTime, formatDateTimeWib } from '../formatters';
import { STEP_STATUS, isEditable } from './procurementConstants';

// Crude Procurement list (Plan & Procurement) — ProcurementValidity contract.
//
// The business stage after Planning: the server only lists Procurements whose
// Planning was Submitted (a ProcurementValidity row exists), so no client-side
// filter is needed. Each record has its own Draft -> Submitted lifecycle; Submit
// advances Main Status to Tender Opening. The list `id` is ProcurementValidity.Id.
//
// Tampilan mengikuti desain acuan: 3 kartu ringkasan status (Open / On Progress
// / Submitted), search + filter pills + dropdown status, dan tabel dengan kolom
// jadwal (Submission Deadline & Validity Period) + tombol "Detail & Schedule".

// Header putih di atas navy — mengikuti gaya header tabel PlanningView.
const headerLabel = label => (
  <span className='text-uppercase fw-semibold fs-13 mb-0 text-white'>
    {label}
  </span>
);

// UI column sortKey -> PascalCase, whitelisted API sortField (contract section 1).
const SORT_FIELD_MAP = {
  procId: 'ProcurementCode',
  title: 'Title',
  procurementType: 'JenisPengadaanName',
  contractType: 'ContractType',
  description: 'Description',
  submissionDeadline: 'SubmissionDeadline',
  validityPeriod: 'ValidityPeriod',
  createdDate: 'Created',
  updatedDate: 'Modified',
  status: 'PhaseStatusName',
};

// Status turunan untuk list & kartu ringkasan (desain acuan):
// - Submitted   : record sudah disubmit (phase status Submitted, atau phase
//                 utamanya sudah lewat dari Procurement).
// - On Progress : masih Draft tapi sudah ada tab yang disimpan (step Completed).
// - Open        : transaksi baru dari Planning, belum ada yang disimpan.
export const PROC_STAGE = {
  OPEN: 'Open',
  ON_PROGRESS: 'On Progress',
  SUBMITTED: 'Submitted',
};

export const deriveStage = row => {
  if (
    row.status === 'Submitted' ||
    (row.mainStatus && row.mainStatus !== 'Procurement')
  ) {
    return PROC_STAGE.SUBMITTED;
  }
  const steps = Object.values(row.detail?.steps || {});
  return steps.some(s => s === STEP_STATUS.COMPLETED)
    ? PROC_STAGE.ON_PROGRESS
    : PROC_STAGE.OPEN;
};

// Pill status di kolom Status — warna mengikuti desain acuan.
const STAGE_PILL = {
  [PROC_STAGE.OPEN]: 'bg-success-subtle text-success border-success-subtle',
  [PROC_STAGE.ON_PROGRESS]:
    'bg-warning-subtle text-warning border-warning-subtle',
  [PROC_STAGE.SUBMITTED]:
    'bg-primary-subtle text-primary border-primary-subtle',
};

const STAGE_ICON = {
  [PROC_STAGE.OPEN]: 'ri-checkbox-circle-line',
  [PROC_STAGE.ON_PROGRESS]: 'ri-time-line',
  [PROC_STAGE.SUBMITTED]: 'ri-file-text-line',
};

const StagePill = ({ stage }) => (
  <span
    className={`badge rounded-pill border ${STAGE_PILL[stage] || 'bg-secondary-subtle text-secondary'}`}
  >
    <i className={`${STAGE_ICON[stage] || 'ri-question-line'} me-2`}></i>
    {stage}
  </span>
);

// Kartu ringkasan di atas list.
const SummaryCard = ({ icon, iconClass, label, count, subtitle, subClass }) => (
  <Card className='border shadow-none mb-0 h-100'>
    <CardBody className='py-3'>
      <div className='d-flex align-items-center'>
        <div className='flex-shrink-0 me-3'>
          <span className={`avatar-title rounded-circle fs-3 ${iconClass}`}>
            <i className={icon}></i>
          </span>
        </div>
        <div>
          <p className='text-muted mb-1 fw-semibold fs-13'>{label}</p>
          <h4 className='mb-1'>{count}</h4>
          <p className={`mb-0 fs-12 fw-semibold ${subClass}`}>{subtitle}</p>
        </div>
      </div>
    </CardBody>
  </Card>
);

// Filter pills jenis pengadaan. Modul ini (crude) hanya berisi Crude &
// Condensate; pill Product disediakan mengikuti desain — hasilnya kosong
// sampai pengadaan Product ikut dilayani backend ini.
const TYPE_FILTERS = ['All', 'Crude & Condensate', 'Product'];
const CARGO_TYPE = 'Crude & Condensate';

const ProcurementView = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Desain acuan membuka list terurut Updated Date terbaru.
  const [sortField, setSortField] = useState('updatedDate');
  const [sortDirection, setSortDirection] = useState('desc');

  const [typeFilter, setTypeFilter] = useState('All');
  const [stageFilter, setStageFilter] = useState('');
  const [summary, setSummary] = useState({
    [PROC_STAGE.OPEN]: 0,
    [PROC_STAGE.ON_PROGRESS]: 0,
    [PROC_STAGE.SUBMITTED]: 0,
  });

  document.title = 'Procurement';

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, sortField, sortDirection]);

  // Kartu ringkasan menghitung SEMUA record (bukan halaman aktif). PageSize
  // dibatasi 100 oleh PaginatedListRequestValidator, jadi ambil per halaman
  // sampai totalCount terpenuhi (cap 20 halaman sebagai pengaman).
  const loadSummary = useCallback(async () => {
    try {
      const pageSize = 100;
      let page = 1;
      let all = [];
      for (;;) {
        const { items: rows, totalCount } = await api.listProcurement({
          page,
          pageSize,
        });
        all = all.concat(rows);
        if (!rows.length || all.length >= totalCount || page >= 20) break;
        page += 1;
      }
      const counts = {
        [PROC_STAGE.OPEN]: 0,
        [PROC_STAGE.ON_PROGRESS]: 0,
        [PROC_STAGE.SUBMITTED]: 0,
      };
      all.forEach(r => {
        counts[deriveStage(r)] += 1;
      });
      setSummary(counts);
    } catch {
      // ringkasan bersifat kosmetik — kegagalan tidak menghalangi list
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { items: rows, totalCount } = await api.listProcurement({
        page: currentPage,
        pageSize: rowsPerPage,
        searchText: debouncedSearch,
        sortField: sortField ? SORT_FIELD_MAP[sortField] : null,
        sortOrder: sortDirection === 'desc' ? 'Desc' : 'Asc',
      });
      setItems(rows);
      setTotalRows(totalCount);
    } catch (error) {
      toast.error(`Failed to load procurement list: ${error}`);
      setItems([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, currentPage, rowsPerPage, sortField, sortDirection]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = (column, direction) => {
    setSortField(column.sortKey || null);
    setSortDirection(direction);
  };

  // Filter type & status diterapkan client-side pada halaman yang sedang tampil
  // (kontrak list belum punya parameter filter status/type).
  const visibleItems = useMemo(
    () =>
      items.filter(row => {
        if (typeFilter === 'Product') return false;
        if (stageFilter && deriveStage(row) !== stageFilter) return false;
        return true;
      }),
    [items, typeFilter, stageFilter]
  );

  // Edit (Draft) opens the Procurement Edit form; View Detail (Submitted) opens
  // the read-only detail page. Satu tombol "Detail & Schedule" untuk keduanya.
  const handleOpen = row =>
    isEditable(row.status)
      ? navigate(`/crude/procurement/create/${row.id}`)
      : navigate(`/crude/procurement/dtl/${row.id}`);

  const columns = [
    {
      name: headerLabel('Id Procurement'),
      sortKey: 'procId',
      sortable: true,
      wrap: true,
      width: '170px',
      selector: row => row.procId,
      cell: row => (
        <span className='fw-semibold font-monospace fs-12'>{row.procId}</span>
      ),
    },
    {
      name: headerLabel('Created Date'),
      sortKey: 'createdDate',
      sortable: true,
      wrap: true,
      width: '150px',
      selector: row => row.createdDate,
      cell: row => (
        <span className='font-monospace fs-12'>
          {formatDateTime(row.createdDate)}
        </span>
      ),
    },
    {
      name: headerLabel('Updated Date'),
      sortKey: 'updatedDate',
      sortable: true,
      wrap: true,
      width: '150px',
      selector: row => row.updatedDate,
      cell: row => (
        <span className='font-monospace fs-12'>
          {formatDateTime(row.updatedDate)}
        </span>
      ),
    },
    {
      name: headerLabel('Title'),
      sortKey: 'title',
      sortable: true,
      wrap: true,
      grow: 2,
      selector: row => row.title,
      cell: row => <span className='fw-semibold'>{row.title}</span>,
    },
    {
      name: headerLabel('Procurement Type'),
      sortKey: 'procurementType',
      sortable: true,
      wrap: true,
      width: '160px',
      selector: row => row.procurementMethod,
    },
    {
      name: headerLabel('Period'),
      sortKey: 'contractType',
      sortable: true,
      center: true,
      width: '140px',
      selector: row => row.contractType,
      cell: row => (
        <span className='fw-semibold'>{row.contractType || '-'}</span>
      ),
    },
    {
      name: headerLabel('Type'),
      center: true,
      width: '160px',
      cell: () => (
        <span className='badge bg-secondary-subtle text-secondary border'>
          {CARGO_TYPE}
        </span>
      ),
    },
    {
      name: headerLabel('Description'),
      sortKey: 'description',
      sortable: true,
      wrap: true,
      grow: 2,
      selector: row => row.description,
      cell: row => (
        <span className='text-primary fs-12'>{row.description || '-'}</span>
      ),
    },
    {
      name: headerLabel('Submission Deadline'),
      sortKey: 'submissionDeadline',
      sortable: true,
      wrap: true,
      width: '210px',
      selector: row => row.submissionDeadline,
      cell: row => (
        <span className='font-monospace fs-12'>
          <i className='ri-time-line text-muted me-2'></i>
          {formatDateTimeWib(row.submissionDeadline)}
        </span>
      ),
    },
    {
      name: headerLabel('Validity Period'),
      sortKey: 'validityPeriod',
      sortable: true,
      wrap: true,
      width: '210px',
      selector: row => row.validityPeriod,
      cell: row => (
        <span className='font-monospace fs-12'>
          <i className='ri-calendar-line text-muted me-2'></i>
          {formatDateTimeWib(row.validityPeriod)}
        </span>
      ),
    },
    {
      name: headerLabel('Status'),
      sortKey: 'status',
      sortable: true,
      center: true,
      width: '140px',
      selector: row => row.status,
      cell: row => <StagePill stage={deriveStage(row)} />,
    },
    {
      name: headerLabel('Action'),
      width: '170px',
      center: true,
      cell: row => (
        <ActionButton
          variant='secondary'
          size='sm'
          icon='ri-pencil-line'
          onClick={() => handleOpen(row)}
        >
          Detail &amp; Schedule
        </ActionButton>
      ),
    },
  ];

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb title='Procurement' pageTitle='Plan & Procurement' />

          {/* ---- Kartu ringkasan status (desain acuan) ---- */}
          <Row className='g-3 mb-3'>
            <Col xl={4} md={4}>
              <SummaryCard
                icon='ri-checkbox-circle-line'
                iconClass='bg-success-subtle text-success'
                label='Status Open'
                count={summary[PROC_STAGE.OPEN]}
                subtitle='Transaksi Baru dari Planning'
                subClass='text-success'
              />
            </Col>
            <Col xl={4} md={4}>
              <SummaryCard
                icon='ri-time-line'
                iconClass='bg-warning-subtle text-warning'
                label='Status On Progress'
                count={summary[PROC_STAGE.ON_PROGRESS]}
                subtitle='Telah Disimpan di Procurement'
                subClass='text-warning'
              />
            </Col>
            <Col xl={4} md={4}>
              <SummaryCard
                icon='ri-file-text-line'
                iconClass='bg-primary-subtle text-primary'
                label='Status Submitted'
                count={summary[PROC_STAGE.SUBMITTED]}
                subtitle='Telah Disubmit di Procurement'
                subClass='text-primary'
              />
            </Col>
          </Row>

          <Row>
            <Col lg={12}>
              <Card id='ProcurementList'>
                <CardBody>
                  {/* ---- Search + filter (desain acuan) ---- */}
                  <div className='d-flex align-items-center flex-wrap gap-2 mb-3'>
                    <div className='search-box flex-grow-1'>
                      <Input
                        type='text'
                        className='form-control'
                        placeholder='Cari ID Procurement, Title, Type, atau Description...'
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                      />
                      <i className='ri-search-line search-icon'></i>
                    </div>
                    <div
                      className='btn-group flex-shrink-0'
                      role='group'
                      aria-label='Filter jenis pengadaan'
                    >
                      {TYPE_FILTERS.map(t => (
                        <button
                          key={t}
                          type='button'
                          className={`btn btn-sm ${
                            typeFilter === t
                              ? 'btn-primary'
                              : 'btn-light border'
                          }`}
                          onClick={() => setTypeFilter(t)}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                    <Input
                      type='select'
                      className='form-select form-select-sm w-auto flex-shrink-0'
                      value={stageFilter}
                      onChange={e => setStageFilter(e.target.value)}
                    >
                      <option value=''>Semua Status Procurement</option>
                      <option value={PROC_STAGE.OPEN}>Open</option>
                      <option value={PROC_STAGE.ON_PROGRESS}>
                        On Progress
                      </option>
                      <option value={PROC_STAGE.SUBMITTED}>Submitted</option>
                    </Input>
                  </div>

                  <div className='table-responsive'>
                    <DataTable
                      columns={columns}
                      data={visibleItems}
                      highlightOnHover
                      pagination
                      paginationServer
                      paginationTotalRows={totalRows}
                      paginationPerPage={rowsPerPage}
                      paginationDefaultPage={currentPage}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      onChangePage={page => setCurrentPage(page)}
                      onChangeRowsPerPage={newPerPage => {
                        setRowsPerPage(newPerPage);
                        setCurrentPage(1);
                      }}
                      sortServer
                      onSort={handleSort}
                      wrap
                      dense
                      striped
                      progressPending={loading}
                      noDataComponent={
                        <div
                          style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6c757d',
                          }}
                        >
                          <i
                            className='ri-database-2-line'
                            style={{
                              fontSize: '32px',
                              marginBottom: '8px',
                              display: 'block',
                            }}
                          ></i>
                          No procurement found
                        </div>
                      }
                      customStyles={{
                        headCells: {
                          style: {
                            // Navy gelap + teks putih mengikuti PlanningView.
                            backgroundColor: '#1e3a56',
                            color: '#ffffff',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            borderRight: '1px solid rgba(255,255,255,0.15)',
                            // Headers stay on one line — they set the column's
                            // real floor, so a wrapped header would let the
                            // column collapse narrower than its own label.
                            whiteSpace: 'nowrap',
                          },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                            borderRight: '1px solid #dee2e6',
                          },
                        },
                        rows: { style: { minHeight: '50px' } },
                      }}
                    />
                    <ToastContainer closeButton={false} limit={1} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ProcurementView;
